import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import forEach from 'lodash/forEach'
import chunk from 'lodash/chunk'
import mapValues from 'lodash/mapValues'
import { mapState } from 'vuex'
import { DateMixin, NumberMixin } from '@kwixeo/interface-vue'
import { VBTooltip } from 'bootstrap-vue'
import moment from 'moment'

@Component({
  computed: {
    ...mapState('dashboard', ['widgets'])
  },
  directives: {
    'b-tooltip': VBTooltip
  }
})
export default class WidgetChronologicalTable extends Mixins(NumberMixin, DateMixin) {
  @Prop({ required: true }) value!: KwixeoDashboard.WidgetChronological
  @Prop({ required: true }) datas!: KwixeoDashboard.DashboardDatas
  @Prop({ required: true }) compare!: boolean
  @Prop({ required: true }) initialized!: boolean

  private widgetWidth = 0

  mounted (): void {
    this.setWidgetWidth()
    window.addEventListener('resize', this.setWidgetWidth)
  }

  destroyed (): void {
    window.removeEventListener('resize', this.setWidgetWidth)
  }

  get isToday (): (i: number, p: number) => boolean {
    return (i, p) => {
      const label = this.chunkLabels[i][p]
      if (/^(\d{2}-\d{2}-\d{4})$/i.test(label)) {
        return moment().format('DD-MM-YYYY') === label
      }
      return false
    }
  }

  @Watch('value', { deep: true })
  private watchWidth (): void {
    this.setWidgetWidth()
  }

  @Watch('initialized')
  private watchInitialized (): void {
    if (this.initialized) {
      this.setWidgetWidth()
    }
  }

  private setWidgetWidth (): void {
    this.widgetWidth = this.$el?.clientWidth ?? 0
  }

  get columns (): number {
    let columns = Math.floor((this.widgetWidth - 100) / 120)
    if (columns < 1) {
      columns = 1
    }
    return columns
  }

  get computedCompare (): boolean {
    if (this.value.params.period !== 'default') {
      return false
    }
    return this.compare
  }

  get dataKey (): (key: string) => string {
    return key => {
      const params = this.value.params

      if (params.filters) {
        mapValues(params.filters, ((filter, index) => {
          key += '-' + index + '-' + filter.join('-')
        }))
      }

      if (params.period) {
        key += '-' + params.period
        if (params.period !== 'default') {
          key += '-' + params.period_value + '-' + params.period_type
        }
      }

      return key
    }
  }

  get availableDatas (): KwixeoDashboard.ChronologicalDatasSources {
    return this.value.params.datas.filter(data => {
      return this.datas[this.dataKey(data.key)] !== null
    })
  }

  get labels (): string[] {
    const datas = this.availableDatas
    if (!datas[0]) {
      return []
    }
    return (this.datas[this.dataKey(datas[0].key)] as KwixeoDashboard.ChronologicalDatas)?.labels.map(label => label.label)
  }

  get chunkLabels (): (string[])[] {
    return chunk(this.labels, this.columns)
  }

  get chunkDatas (): (index: number) => KwixeoDashboard.WidgetChronologicalTableDatas {
    return index => this.chartDatas.map(datas => {
      return JSON.parse(JSON.stringify({
        label: datas.label,
        data: chunk(datas.data, this.columns)[index]
      }))
    })
  }

  get chartDatas (): KwixeoDashboard.WidgetChronologicalTableDatas {
    const datas: KwixeoDashboard.WidgetChronologicalTableDatas = []
    this.availableDatas.forEach(value => {
      const data: KwixeoDashboard.WidgetChronologicalTableDatasOne[] = []
      forEach((this.datas[ this.dataKey(value.key) ] as KwixeoDashboard.ChronologicalDatas).datas, (datas: KwixeoDashboard.WidgetDatas) => {
        data.push({
          value: datas[ value.value ]
        })
      })

      if (this.computedCompare) {
        let index = 0
        forEach((this.datas[ 'compare-' + this.dataKey(value.key) ] as KwixeoDashboard.ChronologicalDatas).datas, (datas: KwixeoDashboard.WidgetDatas) => {
          const previous = datas[ value.value ]
          const actual = data[index].value
          data[index].previous = previous
          data[index].variation = previous === 0 ? 0 : this.roundNumber(((actual - previous) / previous) * 100, 2)
          index++
        })
      }

      datas.push({
        label: value.name,
        data
      })
    })

    return datas
  }

}
